import apiClient from './client';
import { openPdfInTab } from '../utils/pdfPreview';

// ── Tipi (allineati a backend/app/models/fatture_schemas.py) ──────────────────

export interface DaFatturareItem {
  conferimento_id: string;
  numero: string | null;
  numero_fir: string | null;
  order_id: string;
  codice_ordine: string;
  customer_id: string;
  customer_ragione_sociale: string;
  categoria: string;
  data_prevista: string | null;
  peso_netto_kg: number | null;
  peso_per_fatturazione_kg: number | null;
  tariffa_unitaria_eur_kg: number | null;
  cliente_porta_materiale: boolean;
  costo_trasporto_eur_per_viaggio: number | null;
}

// M10.1: una riga di input per conferimento; IVA libera per riga e trasporto
// per-conferimento (override importo + IVA propria). Override assenti → default BE.
export interface AnteprimaRigaInput {
  conferimento_id: string;
  peso_kg?: number;
  tariffa_eur_kg?: number;
  iva_riga?: number;
  importo_trasporto_eur?: number;
  iva_trasporto?: number;
}

// Una voce di fattura restituita dal BE: 'materiale' o 'trasporto'.
export interface AnteprimaVoce {
  conferimento_id: string;
  numero: string | null;
  numero_fir: string | null;
  codice_ordine: string;
  categoria: string;
  tipo: 'materiale' | 'trasporto';
  descrizione: string;
  peso_kg: number | null;
  tariffa_eur_kg: number | null;
  imponibile: number;
  iva: number;          // aliquota %
  iva_importo: number;
  totale: number;
}

// Idoneità FatturaPA: un problema per campo mancante/non valido, con dove compilarlo.
export interface IdoneitaProblema {
  entita: 'cliente' | 'impianto';
  campo: string;
  messaggio: string;
  dove: 'anagrafica cliente' | 'Settings impianto';
}

export interface IdoneitaResult {
  ok: boolean;
  problemi: IdoneitaProblema[];
}

export interface AnteprimaResponse {
  customer_id: string;
  customer_ragione_sociale: string;
  voci: AnteprimaVoce[];
  importo_netto: number;       // imponibile
  importo_trasporto: number;   // Σ voci trasporto
  importo_iva: number;
  importo_totale: number;
  idoneita: IdoneitaResult;
}

export interface EmissioneRigaInput {
  conferimento_id: string;
  peso_kg: number;
  importo_riga_eur: number;
  iva_riga: number;
  importo_trasporto_eur: number;
  iva_trasporto: number;
}

export interface EmissioneRequest {
  // numero ufficiale e ProgressivoInvio sono assegnati dal server all'emissione.
  note?: string | null;
  righe: EmissioneRigaInput[];
}

export type StatoFattura = 'bozza' | 'emessa' | 'pagata' | 'annullata';

/** Canale del documento (V72): recupero rifiuti vs cessione di rifiuti prodotti. */
export type TipoFattura = 'servizio' | 'vendita';

/**
 * Regime IVA di una riga di vendita (V72). `reverse_charge` = inversione
 * contabile ex art. 74 co. 7 DPR 633/72: aliquota 0 + Natura N6.1. Il privato
 * senza P.IVA non puo' assolvere l'imposta e il backend lo forza a ordinaria —
 * qui il tipo li ammette entrambi, la regola non vive nel FE.
 */
export type RegimeIva = 'reverse_charge' | 'iva_ordinaria';
export const REGIMI_IVA: RegimeIva[] = ['reverse_charge', 'iva_ordinaria'];

export interface FatturaResponse {
  id: string;
  numero_interno: string | null;
  numero_fattura: string | null;   // ufficiale FatturaPA (annuale con reset, auto all'emissione)
  progressivo_invio: string | null;  // ProgressivoInvio SdI (auto all'emissione)
  customer_id: string;
  data_emissione: string;
  stato: StatoFattura;
  tipo: TipoFattura;
  /** Scadenza di pagamento; NULL sulle fatture emesse prima della V72. */
  data_scadenza: string | null;
  importo_netto: number;
  importo_trasporto: number;
  importo_totale: number;
  note: string | null;
  xml_path: string | null;
  emessa_at: string | null;
  conferimento_ids: string[];
}

// ── Filtri ────────────────────────────────────────────────────────────────────

export interface DaFatturareFilters {
  customer_id?: string;
  order_id?: string;
  categoria?: string;
  data_da?: string;
  data_a?: string;
}

export interface FattureFilters {
  customer_id?: string;
  stato?: StatoFattura;
  /** Canale: 'servizio' | 'vendita'. Assente = tutte. */
  tipo?: TipoFattura;
}

// ── API ─────────────────────────────────────────────────────────────────────

export async function getDaFatturare(filters: DaFatturareFilters = {}): Promise<DaFatturareItem[]> {
  const { data } = await apiClient.get<DaFatturareItem[]>('/api/fatture/da-fatturare', { params: filters });
  return data;
}

/** Uscita di VENDITA non ancora agganciata a nessuna fattura. */
export interface UscitaDaFatturare {
  uscita_id: string;
  codice: string;
  numero_fir: string;
  data_partenza: string;
  customer_id: string;
  customer_ragione_sociale: string;
  cer_rifiuto_id: string;
  cer_code: string;
  descrizione_rifiuto: string;
  peso_kg: number;
  /** Regime del CATALOGO: il default con cui la riga nascerebbe, non uno snapshot. */
  regime_iva_catalogo: RegimeIva;
}

export async function getUsciteDaFatturare(customerId?: string): Promise<UscitaDaFatturare[]> {
  const { data } = await apiClient.get<UscitaDaFatturare[]>('/api/fatture/uscite-da-fatturare',
    { params: customerId ? { customer_id: customerId } : {} });
  return data;
}

export async function postAnteprima(righe: AnteprimaRigaInput[]): Promise<AnteprimaResponse> {
  const { data } = await apiClient.post<AnteprimaResponse>('/api/fatture/anteprima', { righe });
  return data;
}

// Download del file XML (auth via interceptor → fetch come blob, poi salvataggio client).
export async function getFatturaXmlBlob(id: string): Promise<Blob> {
  const { data } = await apiClient.get(`/api/fatture/${id}/xml`, { responseType: 'blob' });
  return data as Blob;
}

// C10: pro forma di una fattura emessa (PDF JWT-protetto → fetch+blob+nuova scheda).
export async function getProformaEmessaPdf(id: string): Promise<void> {
  await openPdfInTab(`/api/fatture/${id}/proforma-pdf`);
}

export async function emettiFattura(body: EmissioneRequest): Promise<FatturaResponse> {
  const { data } = await apiClient.post<FatturaResponse>('/api/fatture', body);
  return data;
}

export async function listFatture(filters: FattureFilters = {}): Promise<FatturaResponse[]> {
  const { data } = await apiClient.get<FatturaResponse[]>('/api/fatture', { params: filters });
  return data;
}

export async function getFattura(id: string): Promise<FatturaResponse> {
  const { data } = await apiClient.get<FatturaResponse>(`/api/fatture/${id}`);
  return data;
}

// ── Dettaglio fattura attiva (#8): header + righe per-conferimento ─────────────
export interface FatturaRigaDettaglio {
  conferimento_id: string;
  numero: string | null;
  peso_kg: number | null;
  prezzo_unitario_eur_kg: number | null;
  importo_riga_eur: number | null;
}

export interface FatturaVoceExtraDettaglio {
  descrizione: string;
  quantita: number;
  prezzo_unitario_eur: number;
  aliquota_iva: number;
  imponibile: number;
}

/**
 * Riga-uscita di una fattura di VENDITA emessa. Numeri VERI (il BE usa gli alias
 * KgDecimal/MoneyDecimal, #137), a differenza di `FatturaRigaDettaglio` accanto,
 * dove i `number` dichiarati sono il debito censito in CLAUDE.md.
 */
export interface FatturaUscitaDettaglio {
  uscita_id: string;
  /** V76: la lista e' di RIGHE, non di uscite — due righe della stessa uscita
   *  ripetono codice e FIR, ed e' `riga_id` a identificarle. */
  riga_id: string;
  codice: string;
  numero_fir: string;
  data_partenza: string;
  cer_code: string;
  descrizione_rifiuto: string;
  /** Definizione commerciale della riga; null sui sacchi anteriori alla V74. */
  tipologia_nome: string | null;
  peso_kg: number | null;
  prezzo_unitario: number | null;
  importo_riga_eur: number | null;
}

export interface FatturaDettaglio {
  id: string;
  numero_fattura: string | null;
  numero_interno: string | null;
  progressivo_invio: string | null;
  customer_id: string;
  customer_ragione_sociale: string;
  data_emissione: string;
  stato: StatoFattura;
  tipo: TipoFattura;
  data_scadenza: string | null;
  importo_netto: number;
  importo_trasporto: number;
  importo_totale: number;
  note: string | null;
  xml_disponibile: boolean;
  /** Popolato su `tipo === 'servizio'`. */
  righe: FatturaRigaDettaglio[];
  /** Popolato su `tipo === 'vendita'`. */
  uscite: FatturaUscitaDettaglio[];
  voci_extra: FatturaVoceExtraDettaglio[];
}

export async function getFatturaDettaglio(id: string): Promise<FatturaDettaglio> {
  const { data } = await apiClient.get<FatturaDettaglio>(`/api/fatture/${id}/dettaglio`);
  return data;
}

export async function setStatoFattura(id: string, stato: 'pagata' | 'emessa'): Promise<FatturaResponse> {
  const { data } = await apiClient.patch<FatturaResponse>(`/api/fatture/${id}/stato`, { stato });
  return data;
}

export async function annullaFattura(id: string): Promise<FatturaResponse> {
  const { data } = await apiClient.patch<FatturaResponse>(`/api/fatture/${id}/annulla`);
  return data;
}

// ── BOZZA fattura + voci extra (v1.9.0) ───────────────────────────────────────
// NB: i campi Decimal del BE (response_model pydantic v2) serializzano come STRINGA
// in JSON ("350.00"). Vanno coercizzati con Number() prima di formattare/calcolare.

// Override dicitura a 3 stati (allineato al BE).
export type DicituraModo = 'predefinita' | 'nessuna' | 'manuale';

export interface BozzaConferimento {
  conferimento_id: string;
  numero: string | null;
  numero_fir: string | null;
  codice_ordine: string;
  categoria: string;
  cer_code: string | null;
  peso_kg: string | null;
  tariffa_eur_kg: string | null;
  imponibile_materiale: string;
  iva_materiale: string;
  /** Trasporto EFFETTIVO: override se c'e', tariffa dell'ordine altrimenti. */
  imponibile_trasporto: string;
  /**
   * Override esplicito; `null` = il trasporto viene dalla tariffa dell'ordine.
   * Serve a distinguere «0 perche' l'ordine non prevede trasporto» da «0 perche'
   * azzerato di proposito», e ad abilitare «torna alla tariffa».
   */
  importo_trasporto_override: number | null;
  iva_trasporto: string;
  dicitura_materiale_modo: DicituraModo;
  dicitura_materiale_manuale: string | null;
  dicitura_trasporto_modo: DicituraModo;
  dicitura_trasporto_manuale: string | null;
}

export interface VoceExtra {
  id: string;
  descrizione: string;
  quantita: string;
  prezzo_unitario_eur: string;
  aliquota_iva: string;
  imponibile: string;
  iva_importo: string;
  totale: string;
  ordinamento: number;
  dicitura_modo: DicituraModo;
  dicitura_manuale: string | null;
}

/**
 * Riga-uscita in bozza di vendita (V72). I campi sono SNAPSHOT presi quando la
 * riga e' entrata in bozza: cambiare il regime del catalogo non riscrive una
 * bozza aperta.
 *
 * TIPI JSON, non uniformi per una ragione: `peso_*` sono `KgDecimal` (numero),
 * mentre prezzo/aliquota/importi sono `Decimal` nudi e arrivano come STRINGA.
 * Coercizzare con `Number()` prima di calcolare o formattare.
 */
/** Un big bag che compone una riga: da dove viene il peso. */
export interface BozzaUscitaBigBag {
  id: string;
  qr_token: string;
  peso_kg: number;
  data_produzione: string | null;
}

/**
 * UNA RIGA di fattura: (uscita, tipologia) — V76.
 *
 * I campi numerici sono `number` e dicono il vero (alias #137 lato backend):
 * nascono con questo modello e non entrano nel debito dei `Decimal` nudi.
 */
export interface BozzaUscitaRiga {
  riga_id: string;
  tipologia_id: string | null;
  /** null = sacchi anteriori alla V74; l'etichetta la mette il frontend. */
  tipologia_nome: string | null;
  /** Somma dei sacchi della tipologia: immutabile, e' il dato di partenza. */
  peso_big_bag_kg: number;
  /** Peso FATTURATO: correggibile, puo' divergere dalla somma dei sacchi. */
  peso_fatturazione_kg: number;
  /** €/kg; null = da compilare, l'emissione lo rifiuta mancante. */
  prezzo_unitario: number | null;
  imponibile: number;
  iva_importo: number;
  totale: number;
  big_bags: BozzaUscitaBigBag[];
}

export interface BozzaUscita {
  uscita_id: string;
  codice: string;
  numero_fir: string;
  data_partenza: string;
  cer_code: string;
  descrizione_rifiuto: string;
  /** Peso reale dei big bag: immutabile, e' il registro. */
  peso_kg: number;
  /** Le righe di fatturazione, una per tipologia dei big bag dell'uscita. */
  righe: BozzaUscitaRiga[];
  regime_iva: RegimeIva;
  aliquota_iva: string;
  natura: string | null;
  dicitura: string | null;
  imponibile: string;
  iva_importo: string;
  totale: string;
}

export interface BozzaResponse {
  id: string;
  customer_id: string;
  customer_ragione_sociale: string;
  customer_email: string | null;   // email anagrafica: prefill destinatario pro forma
  created_at: string | null;       // R5/F1: distingue bozze multiple dello stesso cliente
  stato: string;
  note: string | null;
  tipo: TipoFattura;
  /** Scadenza esplicita; null = default d'impianto applicato all'emissione. */
  data_scadenza: string | null;
  /** Regime della bozza di vendita (uno solo per fattura); null sul servizio. */
  regime_iva: RegimeIva | null;
  conferimenti: BozzaConferimento[];
  uscite: BozzaUscita[];
  voci_extra: VoceExtra[];
  importo_netto: string;
  importo_iva: string;
  importo_totale: string;
  metodo_pagamento_override: string | null;
  metodo_pagamento_default: string;
  idoneita: IdoneitaResult;   // C7: campi mancanti FatturaPA (banner proattivo)
}

// Metodi di pagamento overridabili (allineati al Literal BE e alle label i18n).
export const METODI_PAGAMENTO = ['MP01', 'MP02', 'MP05', 'MP08', 'MP12', 'MP19', 'MP23'] as const;
export type MetodoPagamento = typeof METODI_PAGAMENTO[number];

export interface VoceExtraInput {
  descrizione: string;
  quantita: number;
  prezzo_unitario_eur: number;   // può essere negativo (sconto)
  /** >= 0. Lo 0 vale solo in inversione contabile, dove il backend la
   *  ignora e la riscrive con la Natura: su una bozza di servizio e' 422. */
  aliquota_iva: number;
  ordinamento?: number;
  dicitura_modo?: DicituraModo;
  dicitura_manuale?: string | null;
}

export interface ProformaInviaInput {
  email_to: string;
  cc?: string[] | null;
  subject?: string | null;
  body?: string | null;
}

export async function creaBozza(conferimentoIds: string[]): Promise<BozzaResponse> {
  const { data } = await apiClient.post<BozzaResponse>('/api/fatture/bozza', { righe: conferimentoIds });
  return data;
}

/**
 * Bozza di VENDITA su N uscite. Canale SEPARATO da `creaBozza`: i due campi si
 * escludono lato backend, perche' una fattura e' di servizio o di vendita e
 * mescolarle darebbe un documento con due regimi e due discipline.
 *
 * 409 se il cliente ha gia' una bozza vendita aperta (indice parziale V72).
 */
export async function creaBozzaVendita(uscitaIds: string[]): Promise<BozzaResponse> {
  const { data } = await apiClient.post<BozzaResponse>('/api/fatture/bozza', { uscita_ids: uscitaIds });
  return data;
}

/**
 * Regime IVA di UN'USCITA. Peso e prezzo non passano piu' di qui: dalla V76
 * sono della RIGA, perche' e' la tipologia a decidere il prezzo.
 */
export async function aggiornaRegimeUscitaBozza(
  id: string, uscitaId: string, regime_iva: RegimeIva,
): Promise<BozzaResponse> {
  const { data } = await apiClient.patch<BozzaResponse>(
    `/api/fatture/bozza/${id}/uscite/${uscitaId}`, { regime_iva });
  return data;
}

/** Peso fatturato e prezzo di UNA riga (uscita, tipologia). */
export async function aggiornaRigaUscitaBozza(
  id: string, uscitaId: string, rigaId: string,
  patch: { peso_fatturazione_kg?: number; prezzo_unitario?: number },
): Promise<BozzaResponse> {
  const { data } = await apiClient.patch<BozzaResponse>(
    `/api/fatture/bozza/${id}/uscite/${uscitaId}/righe/${rigaId}`, patch);
  return data;
}

/**
 * Regime IVA dell'INTERA bozza di vendita: righe-uscita E voci extra.
 *
 * Il PATCH per riga vale solo su una bozza a UNA riga: con 2+ righe il backend
 * rifiuta per costruzione (la prima riga toccata e' in regime diverso dalle
 * altre, in qualunque ordine la si cambi). 422 se il cliente e' un privato.
 */
export async function aggiornaRegimeBozza(id: string, regime: RegimeIva): Promise<BozzaResponse> {
  const { data } = await apiClient.patch<BozzaResponse>(
    `/api/fatture/bozza/${id}/regime`, { regime_iva: regime });
  return data;
}

export async function rimuoviUscitaBozza(id: string, uscitaId: string): Promise<BozzaResponse> {
  const { data } = await apiClient.delete<BozzaResponse>(`/api/fatture/bozza/${id}/uscite/${uscitaId}`);
  return data;
}

/** Scadenza esplicita della bozza; null = torna al default d'impianto. Vale anche sul servizio. */
export async function aggiornaScadenzaBozza(id: string, dataScadenza: string | null): Promise<BozzaResponse> {
  const { data } = await apiClient.patch<BozzaResponse>(
    `/api/fatture/bozza/${id}/scadenza`, { data_scadenza: dataScadenza });
  return data;
}

export async function getBozza(id: string): Promise<BozzaResponse> {
  const { data } = await apiClient.get<BozzaResponse>(`/api/fatture/bozza/${id}`);
  return data;
}

export async function getBozze(customerId?: string): Promise<BozzaResponse[]> {
  const { data } = await apiClient.get<BozzaResponse[]>('/api/fatture/bozze',
    { params: customerId ? { customer_id: customerId } : {} });
  return data;
}

export async function scartaBozza(id: string): Promise<void> {
  await apiClient.delete(`/api/fatture/bozza/${id}`);
}

export async function aggiungiConferimentiBozza(id: string, conferimentoIds: string[]): Promise<BozzaResponse> {
  const { data } = await apiClient.post<BozzaResponse>(`/api/fatture/bozza/${id}/conferimenti`, { righe: conferimentoIds });
  return data;
}

export async function rimuoviConferimentoBozza(id: string, conferimentoId: string): Promise<BozzaResponse> {
  const { data } = await apiClient.delete<BozzaResponse>(`/api/fatture/bozza/${id}/conferimenti/${conferimentoId}`);
  return data;
}

// Override IVA per-conferimento in bozza (due campi: materiale e trasporto).
export async function aggiornaIvaConferimentoBozza(
  id: string, conferimentoId: string, body: { iva_riga: number; iva_trasporto: number },
): Promise<BozzaResponse> {
  const { data } = await apiClient.patch<BozzaResponse>(
    `/api/fatture/bozza/${id}/conferimenti/${conferimentoId}/iva`, body);
  return data;
}

/**
 * Importo del TRASPORTO di una riga-conferimento in bozza.
 *
 * Tre stati, come lato backend: `undefined` non si manda (il chiamante non
 * chiama), `null` riporta alla tariffa dell'ordine, un numero e' l'override —
 * e lo ZERO e' un override valido, non un campo vuoto.
 */
export async function aggiornaTrasportoConferimentoBozza(
  id: string, conferimentoId: string, importo: number | null,
): Promise<BozzaResponse> {
  const { data } = await apiClient.patch<BozzaResponse>(
    `/api/fatture/bozza/${id}/conferimenti/${conferimentoId}/trasporto`,
    { importo_trasporto_eur: importo });
  return data;
}

// Override dicitura per riga-da-conferimento (materiale/trasporto), scoped alla bozza.
export async function aggiornaDicituraConferimentoBozza(
  id: string, conferimentoId: string,
  body: { tipo: 'materiale' | 'trasporto'; dicitura_modo: DicituraModo; dicitura_manuale?: string | null },
): Promise<BozzaResponse> {
  const { data } = await apiClient.patch<BozzaResponse>(
    `/api/fatture/bozza/${id}/conferimenti/${conferimentoId}/dicitura`, body);
  return data;
}

// Override metodo di pagamento (MPxx) sulla bozza; null = reset al default impianto.
export async function aggiornaMetodoPagamentoBozza(
  id: string, metodo: MetodoPagamento | null,
): Promise<BozzaResponse> {
  const { data } = await apiClient.patch<BozzaResponse>(
    `/api/fatture/bozza/${id}/metodo-pagamento`, { metodo_pagamento_override: metodo });
  return data;
}

export async function aggiungiVoceExtra(id: string, voce: VoceExtraInput): Promise<BozzaResponse> {
  const { data } = await apiClient.post<BozzaResponse>(`/api/fatture/bozza/${id}/voci-extra`, voce);
  return data;
}

export async function modificaVoceExtra(id: string, rigaId: string, patch: Partial<VoceExtraInput>): Promise<BozzaResponse> {
  const { data } = await apiClient.patch<BozzaResponse>(`/api/fatture/bozza/${id}/voci-extra/${rigaId}`, patch);
  return data;
}

export async function eliminaVoceExtra(id: string, rigaId: string): Promise<BozzaResponse> {
  const { data } = await apiClient.delete<BozzaResponse>(`/api/fatture/bozza/${id}/voci-extra/${rigaId}`);
  return data;
}

export async function emettiDaBozza(id: string): Promise<FatturaResponse> {
  const { data } = await apiClient.post<FatturaResponse>(`/api/fatture/bozza/${id}/emetti`);
  return data;
}

export async function inviaProforma(id: string, body: ProformaInviaInput): Promise<{ detail: string; email_inviata: boolean }> {
  const { data } = await apiClient.post(`/api/fatture/bozza/${id}/proforma/invia`, body);
  return data as { detail: string; email_inviata: boolean };
}
